import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ChevronDown, LogIn, LayoutDashboard, LogOut, Crown } from "lucide-react";
import LayoutToggle from "@/components/LayoutToggle";
import { BrandName } from "@/components/BrandName";
import crest from "@/assets/header-crest.png";
import marble from "@/assets/header-marble.jpg";

const Header = () => {
  const { user, signOut } = useAuth();

  return (
    <header className="relative z-50 bg-background">
      <img src={marble} alt="" aria-hidden className="pointer-events-none absolute inset-0 h-full w-full object-cover" />
      {/* Greek key band */}
      <svg aria-hidden className="pointer-events-none absolute left-1/2 bottom-1 h-3 w-[70%] -translate-x-1/2 text-gold md:bottom-auto md:top-1/2 md:h-4 md:w-[34%] md:-translate-y-1/2" style={{ maskImage: "linear-gradient(90deg, transparent, black 25%, black 75%, transparent)", WebkitMaskImage: "linear-gradient(90deg, transparent, black 25%, black 75%, transparent)", opacity: 0.55 }}>
        <defs>
          <pattern id="greekkey" width="16" height="16" patternUnits="userSpaceOnUse">
            <path d="M0 15H15V1H4V11H11V5H8" fill="none" stroke="currentColor" strokeWidth="1.4" />
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#greekkey)" />
      </svg>
      {/* Carved gold border */}
      <div aria-hidden className="pointer-events-none absolute inset-x-0 bottom-0 h-px bg-gold/60" />
      <nav className="relative w-full h-16 sm:h-20 px-4 sm:px-8 lg:px-12 flex items-center">
        <a
          href="/"
          className="min-w-0 flex items-center gap-2 sm:gap-3"
          data-analytics-event="nav_click"
          data-analytics-location="header"
          data-analytics-label="brand_home"
        >
          <img src={crest} alt="" width={816} height={816} className="h-10 w-10 sm:h-14 sm:w-14 shrink-0 object-contain drop-shadow" />
          <BrandName className="block font-display font-semibold text-[13px] sm:text-base md:text-lg text-gold-dark tracking-[0.12em] uppercase leading-none truncate" />


        </a>

        <div className="flex items-center shrink-0 gap-2 ml-auto">
          <LayoutToggle />
          {!user && (
            <Link to="/auth" className="font-display text-base font-medium text-bronze-dark hover:text-gold-dark px-2">
              Sign in
            </Link>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="h-auto p-2 hover:bg-transparent"
                aria-label="Open menu"
                data-analytics-event="header_crown_open"
              >
                <span className="grid h-10 w-10 place-items-center rounded-full border border-gold shadow-md" style={{ background: "radial-gradient(circle at 35% 30%, hsl(var(--bronze)), hsl(var(--bronze-dark)))" }}><Crown className="h-5 w-5 text-gold-light" /></span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              {user ? (
                <>
                  <DropdownMenuItem asChild>
                    <Link
                      to="/dashboard"
                      className="flex items-center gap-2 cursor-pointer font-semibold text-foreground"
                      data-analytics-event="nav_click"
                      data-analytics-location="header_menu"
                      data-analytics-label="remnant_portal_authed"
                    >
                      <LayoutDashboard className="h-4 w-4 text-gold" />
                      <span>Kingdom Hub</span>
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={() => signOut()}
                    data-analytics-event="nav_click"
                    data-analytics-location="header_menu"
                    data-analytics-label="sign_out"
                    className="flex items-center gap-2 cursor-pointer text-destructive focus:text-destructive"
                  >
                    <LogOut className="h-4 w-4" />
                    Sign Out
                  </DropdownMenuItem>
                </>
              ) : (
                <>
                  <DropdownMenuItem asChild>
                    <Link
                      to="/dashboard"
                      className="flex items-center gap-2 cursor-pointer font-semibold text-foreground"
                      data-analytics-event="nav_click"
                      data-analytics-location="header_menu"
                      data-analytics-label="remnant_portal_guest"
                    >
                      <LogIn className="h-4 w-4 text-gold" />
                      <span>Kingdom Hub</span>
                    </Link>
                  </DropdownMenuItem>
                </>
              )}

            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </nav>
    </header>
  );
};

export default Header;
